import { useCheckout } from 'context/checkout/Checkout';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import colors from 'theme/colors';
import { Check, ChevronDown, Copy, QrCode as QrCodeIcon, ShieldCheck } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import breakpoints from 'theme/breakpoints';
import pusherService from 'services/pusher';
import ExpirationModal from '../commom/ExpirationModal';
import QRCode from 'qrcode.react';
import { Link, useNavigate } from 'react-router-dom';
import paths from 'routes/paths';
import PaymentSuccess from './PaymentSuccess';
import checkoutApi from 'api/requests/checkout';
import currency from 'helpers/currency';

// Marcos em que o tempo restante é anunciado para leitores de tela.
// Anunciar a cada segundo tornaria a região aria-live inutilizável.
const ANNOUNCED_MARKS = {
    600: '10 minutos',
    300: '5 minutos',
    120: '2 minutos',
    60: '1 minuto',
};

const WARNING_THRESHOLD = 300;
const CRITICAL_THRESHOLD = 120;

export default function PixCode({ time, tickets, totalPrice, onPaymentSuccess, skipSuccessModal }) {
    const { checkout } = useCheckout();
    const navigate = useNavigate();

    const initialTime = Math.max(0, Number(time) > 0 ? Number(time) - 5 : 0);

    const [timeLeft, setTimeLeft] = useState(initialTime);
    const [totalTime, setTotalTime] = useState(initialTime);
    const [pixCode, setPixCode] = useState();
    const [paid, setPaid] = useState(false);
    const [expirationModalVisible, setExpirationModalVisible] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copyError, setCopyError] = useState('');
    const [qrOpen, setQrOpen] = useState(false);
    const [checking, setChecking] = useState(false);
    const [checkFeedback, setCheckFeedback] = useState('');
    const [announcement, setAnnouncement] = useState('');

    const copyResetRef = useRef(null);

    // `time` pode chegar depois da montagem (vem do TimeWarning via callback).
    // Sem esta ressincronização o contador nasceria em 0 e abriria o modal de expiração na hora.
    useEffect(() => {
        const parsed = Math.max(0, Number(time) > 0 ? Number(time) - 5 : 0);
        if (parsed > 0 && totalTime === 0) {
            setTimeLeft(parsed);
            setTotalTime(parsed);
        }
    }, [time, totalTime]);

    useEffect(() => {
        // Pagamento confirmado encerra a contagem: manter o cronômetro correndo
        // atrás da confirmação faria a tela expirar uma compra já paga.
        if (totalTime === 0 || paid) {
            return undefined;
        }

        if (timeLeft <= 0) {
            setExpirationModalVisible(true);
            return undefined;
        }

        const interval = setInterval(() => {
            setTimeLeft(previous => previous - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [timeLeft, totalTime, paid]);

    useEffect(() => {
        if (ANNOUNCED_MARKS[timeLeft]) {
            setAnnouncement(`Restam ${ANNOUNCED_MARKS[timeLeft]} para efetuar o pagamento.`);
        }
    }, [timeLeft]);

    useEffect(() => {
        setPixCode(checkout?.pix?.key);
    }, [checkout?.pix?.key]);

    useEffect(() => {
        if (!checkout?.orderId) {
            return undefined;
        }

        const channel = `order.${String(checkout.orderId)}`;
        const handleEvent = () => confirmPayment();

        pusherService.subscribe(channel, 'order.status.updated', handleEvent);

        return () => pusherService.unsubscribe?.(channel, 'order.status.updated', handleEvent);
    }, [checkout?.orderId]);

    useEffect(() => () => clearTimeout(copyResetRef.current), []);

    const orderItems = useMemo(() => {
        if (!Array.isArray(tickets) || !tickets.length) {
            return [];
        }

        const groups = new Map();

        tickets.forEach((item) => {
            const key = item?.id ?? item?.name;
            const group = groups.get(key);

            if (group) {
                group.quantity += 1;
                return;
            }

            groups.set(key, { key, name: item?.name || 'Item', quantity: 1 });
        });

        return [...groups.values()];
    }, [tickets]);

    const progress = totalTime > 0 ? timeLeft / totalTime : 0;
    const isCritical = timeLeft > 0 && timeLeft <= CRITICAL_THRESHOLD;
    const isWarning = !isCritical && timeLeft > 0 && timeLeft <= WARNING_THRESHOLD;
    const timerTone = isCritical ? 'critical' : isWarning ? 'warning' : 'default';
    const formattedTotal = Number(totalPrice) > 0 ? currency.format(Number(totalPrice)) : null;

    function formatTime(seconds) {
        const safeSeconds = Math.max(0, seconds);
        const minutes = Math.floor(safeSeconds / 60);
        const secs = safeSeconds % 60;
        return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
    }

    function confirmPayment() {
        if (skipSuccessModal) {
            onPaymentSuccess?.();
            return;
        }

        // Em vez de abrir uma camada nova, a própria tela muda de estado:
        // o contador para, o código sai e o conteúdo vira a confirmação.
        setPaid(true);
    }

    function goToWallet() {
        navigate(paths.user.wallet);
    }

    function goToHome() {
        onPaymentSuccess?.();
    }

    function flagCopied() {
        setCopyError('');
        setCopied(true);
        clearTimeout(copyResetRef.current);
        copyResetRef.current = setTimeout(() => setCopied(false), 2500);
    }

    function copyWithFallback(value) {
        const input = document.createElement('textarea');
        input.value = value;
        input.setAttribute('readonly', '');
        input.style.position = 'absolute';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();

        const copiedWithFallback = document.execCommand('copy');
        document.body.removeChild(input);

        if (copiedWithFallback) {
            flagCopied();
            return;
        }

        setCopyError('Não conseguimos copiar automaticamente. Selecione o código acima e copie manualmente.');
    }

    async function copyToClipboard() {
        if (!pixCode) {
            return;
        }

        // A Clipboard API não existe em contexto inseguro nem em navegadores embutidos
        // (in-app do Instagram, WebViews antigas). Nesses casos caímos no fallback
        // em vez de deixar o clique morrer sem resposta.
        if (!navigator?.clipboard?.writeText) {
            copyWithFallback(pixCode);
            return;
        }

        try {
            await navigator.clipboard.writeText(pixCode);
            flagCopied();
        } catch {
            copyWithFallback(pixCode);
        }
    }

    async function verifyPayment() {
        if (!checkout?.orderId || checking) {
            return;
        }

        setChecking(true);
        setCheckFeedback('');

        try {
            const { data } = await checkoutApi.getOrderTickets(checkout.orderId, 'unused');

            if (data?.data?.length) {
                confirmPayment();
                return;
            }

            setCheckFeedback('Ainda não identificamos seu pagamento. Se você acabou de pagar, aguarde alguns instantes.');
        } catch {
            setCheckFeedback('Não foi possível verificar agora. Tente novamente em instantes.');
        } finally {
            setChecking(false);
        }
    }

    // Sem stepper aqui: o fluxo acabou, então o indicador de progresso vira ruído
    // e o roxo de "etapa concluída" competiria com o verde da confirmação.
    if (paid) {
        return (
            <Container>
                <PaymentSuccess
                    variant="confirmed"
                    amount={totalPrice}
                    items={tickets}
                    onClose={goToHome}
                    onGoToWallet={goToWallet}
                />
            </Container>
        );
    }

    return (
        <Container>
            <StepIndicator aria-label="Etapa 2 de 2: pagamento">
                <StepCircle done aria-hidden="true">
                    <Check size={16} color={colors.neutralLight} />
                </StepCircle>
                <StepConnector aria-hidden="true" />
                <StepCircle active aria-hidden="true">2</StepCircle>
            </StepIndicator>

            <StatusCard>
                <StatusRow>
                    <StatusPulse aria-hidden="true" />
                    <StatusLabel>Aguardando pagamento</StatusLabel>
                </StatusRow>

                {formattedTotal && <AmountValue>{formattedTotal}</AmountValue>}

                {!!orderItems.length && (
                    <OrderSummary>
                        {orderItems.map(item => (
                            <OrderItem key={item.key}>
                                {item.quantity}× {item.name}
                            </OrderItem>
                        ))}
                    </OrderSummary>
                )}
            </StatusCard>

            <PayGrid>
                <QrSection>
                    <QrToggle
                        type="button"
                        onClick={() => setQrOpen(previous => !previous)}
                        aria-expanded={qrOpen}
                        aria-controls="pix-qr-panel"
                    >
                        <QrCodeIcon size={18} color={colors.occasionalPurple} />
                        Prefere escanear? Ver QR code
                        <ChevronRotate open={qrOpen} aria-hidden="true">
                            <ChevronDown size={18} color={colors.night} />
                        </ChevronRotate>
                    </QrToggle>

                    <QrPanel id="pix-qr-panel" open={qrOpen}>
                        <QrCodeCard>
                            {pixCode ? (
                                <QRCode
                                    value={pixCode}
                                    size={200}
                                    bgColor="#ffffff"
                                    fgColor="#000000"
                                    level="Q"
                                    includeMargin={false}
                                />
                            ) : (
                                <QrSkeleton aria-hidden="true" />
                            )}
                        </QrCodeCard>
                        <QrCaption>
                            Escaneie com a câmera do app do seu banco. No celular, use o código copia e cola.
                        </QrCaption>
                    </QrPanel>
                </QrSection>

                <CodeSection>
                    <SectionTitle>Código copia e cola</SectionTitle>

                    <CodeLabel htmlFor="pix-copy-paste-code">
                        Código PIX para pagamento
                    </CodeLabel>

                    {pixCode ? (
                        <CodeField
                            id="pix-copy-paste-code"
                            readOnly
                            rows={3}
                            value={pixCode}
                            onFocus={(event) => event.target.select()}
                            spellCheck={false}
                        />
                    ) : (
                        <CodeSkeleton aria-hidden="true" />
                    )}

                    <CopyButton
                        type="button"
                        onClick={copyToClipboard}
                        disabled={!pixCode}
                        copied={copied}
                        aria-label={copied ? 'Código PIX copiado' : 'Copiar código PIX'}
                    >
                        {copied ? <Check size={18} /> : <Copy size={18} />}
                        {copied ? 'Código copiado' : 'Copiar código PIX'}
                    </CopyButton>

                    <StatusMessage role="status">
                        {copied && !copyError && 'Agora é só colar no app do seu banco.'}
                        {copyError}
                    </StatusMessage>
                </CodeSection>
            </PayGrid>

            <StepsSection>
                <SectionTitle>Como pagar</SectionTitle>
                <StepsList>
                    <StepItem>Abra o aplicativo do seu banco ou carteira digital.</StepItem>
                    <StepItem>Escolha <strong>PIX</strong> e depois <strong>Copia e Cola</strong>.</StepItem>
                    <StepItem>
                        {formattedTotal
                            ? <>Cole o código e confira o valor: <strong>{formattedTotal}</strong>.</>
                            : 'Cole o código e confira o valor do pedido.'}
                    </StepItem>
                    <StepItem>Confirme o pagamento. A confirmação aparece aqui automaticamente.</StepItem>
                </StepsList>
            </StepsSection>

            <TimerSection>
                <TimerRow>
                    <TimerText tone={timerTone}>
                        {isCritical ? 'Atenção: o código expira em ' : 'Este código expira em '}
                        <TimerValue role="timer" aria-live="off" tone={timerTone}>
                            {formatTime(timeLeft)}
                        </TimerValue>
                    </TimerText>
                </TimerRow>

                <ProgressTrack
                    role="progressbar"
                    aria-label="Tempo restante para pagar"
                    aria-valuemin={0}
                    aria-valuemax={totalTime}
                    aria-valuenow={Math.max(0, timeLeft)}
                    aria-valuetext={`${formatTime(timeLeft)} restantes`}
                >
                    <ProgressFill progress={progress} tone={timerTone} />
                </ProgressTrack>
            </TimerSection>

            <FooterSection>
                <VerifyButton type="button" onClick={verifyPayment} disabled={checking}>
                    {checking ? 'Verificando pagamento…' : 'Já paguei · verificar agora'}
                </VerifyButton>

                {checkFeedback && (
                    <FeedbackText role="status">{checkFeedback}</FeedbackText>
                )}

                <SafeExitNote>
                    <ShieldCheck size={16} color={colors.night} />
                    <span>
                        Pode fechar esta tela: seu pedido continua reservado e o código
                        segue disponível em <SafeExitLink to={paths.user.wallet}>Meus ingressos</SafeExitLink>.
                    </span>
                </SafeExitNote>
            </FooterSection>

            <VisuallyHidden aria-live="polite">{announcement}</VisuallyHidden>


            {expirationModalVisible && <ExpirationModal visible={expirationModalVisible} />}
        </Container>
    );
}

const warningColor = '#A25C00';
const criticalColor = '#C2333C';

const pulse = keyframes`
    0%   { transform: scale(1);   opacity: 1; }
    70%  { transform: scale(2.4); opacity: 0; }
    100% { transform: scale(2.4); opacity: 0; }
`;

const shimmer = keyframes`
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 720px;
    margin: 24px auto 0;
    padding: 0 20px 20px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 0 32px 32px;
    }
`;

const StepIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
`;

const StepCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;

    ${({ active, done }) => (active || done) ? `
        background: ${colors.occasionalPurple};
        color: ${colors.neutralLight};
    ` : `
        background: ${colors.neutralDark};
        color: ${colors.night};
    `}
`;

const StepConnector = styled.div`
    width: 60px;
    height: 2px;
    background: ${colors.occasionalPurple};
`;

const StatusCard = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 20px 16px;
    border-radius: 14px;
    background: #FAF5FF;
    border: 1px solid ${colors.occasionalPurple}22;
`;

const StatusRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StatusPulse = styled.span`
    position: relative;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${colors.occasionalPurple};
    flex-shrink: 0;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: ${colors.occasionalPurple};
        animation: ${pulse} 1.8s ease-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
        &::after {
            animation: none;
        }
    }
`;

const StatusLabel = styled.span`
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: ${colors.occasionalPurple};
`;

const AmountValue = styled.strong`
    font-size: 32px;
    line-height: 1.2;
    font-weight: 700;
    color: ${colors.nightDark};
    margin-top: 10px;
`;

const OrderSummary = styled.ul`
    list-style: none;
    margin: 6px 0 0;
    padding: 0;
`;

const OrderItem = styled.li`
    font-size: 14px;
    color: ${colors.night};
    line-height: 1.5;
`;

const PayGrid = styled.div`
    display: grid;
    gap: 20px;
    margin-top: 24px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: 240px 1fr;
        align-items: start;
    }
`;

const QrSection = styled.section`
    order: 2;

    @media screen and (min-width: ${breakpoints.md}) {
        order: 0;
    }
`;

const QrToggle = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 12px 14px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    background: ${colors.neutralLight};
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        display: none;
    }
`;

const ChevronRotate = styled.span`
    display: flex;
    margin-left: auto;
    transition: transform 0.2s ease;
    transform: rotate(${({ open }) => open ? '180deg' : '0deg'});
`;

const QrPanel = styled.div`
    display: ${({ open }) => open ? 'flex' : 'none'};
    flex-direction: column;
    align-items: center;
    margin-top: 12px;

    @media screen and (min-width: ${breakpoints.md}) {
        display: flex;
        margin-top: 0;
    }
`;

const QrCodeCard = styled.figure`
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 16px;
`;

const QrSkeleton = styled.div`
    width: 200px;
    height: 200px;
    border-radius: 8px;
    background: linear-gradient(90deg, ${colors.neutralDark} 25%, ${colors.neutral} 50%, ${colors.neutralDark} 75%);
    background-size: 800px 100%;
    animation: ${shimmer} 1.4s linear infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const QrCaption = styled.figcaption`
    font-size: 12px;
    line-height: 1.5;
    color: ${colors.night};
    text-align: center;
    margin-top: 10px;
    max-width: 232px;
`;

const CodeSection = styled.section`
    order: 1;
    display: flex;
    flex-direction: column;
`;

const SectionTitle = styled.h2`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0 0 10px;
`;

const CodeLabel = styled.label`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
`;

const CodeField = styled.textarea`
    width: 100%;
    resize: none;
    padding: 12px 14px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    background: #FAFAFA;
    color: ${colors.nightDark};
    font-family: 'SF Mono', 'Roboto Mono', Menlo, Consolas, monospace;
    font-size: 13px;
    line-height: 1.6;
    word-break: break-all;

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 1px;
    }
`;

const CodeSkeleton = styled.div`
    width: 100%;
    height: 78px;
    border-radius: 10px;
    background: linear-gradient(90deg, ${colors.neutralDark} 25%, ${colors.neutral} 50%, ${colors.neutralDark} 75%);
    background-size: 800px 100%;
    animation: ${shimmer} 1.4s linear infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const CopyButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 48px;
    margin-top: 12px;
    padding: 12px 16px;
    border: none;
    border-radius: 10px;
    background: ${({ copied }) => copied ? colors.successDark : colors.occasionalPurple};
    color: ${colors.neutralLight};
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s ease, opacity 0.15s ease;

    &:hover:not(:disabled) {
        opacity: 0.92;
    }

    &:disabled {
        background: ${colors.neutralDark};
        color: ${colors.night};
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${colors.nightDark};
        outline-offset: 2px;
    }
`;

const StatusMessage = styled.p`
    min-height: 18px;
    margin: 8px 0 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.night};
`;

const StepsSection = styled.section`
    margin-top: 24px;
    padding: 18px 20px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
`;

const StepsList = styled.ol`
    margin: 0;
    padding-left: 20px;
`;

const StepItem = styled.li`
    font-size: 14px;
    line-height: 1.7;
    color: ${colors.night};

    strong {
        color: ${colors.nightDark};
    }
`;

const TimerSection = styled.div`
    margin-top: 24px;
`;

const toneColor = (tone) => {
    if (tone === 'critical') return criticalColor;
    if (tone === 'warning') return warningColor;
    return colors.night;
};

const TimerRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const TimerText = styled.p`
    margin: 0 0 8px;
    font-size: 13px;
    color: ${({ tone }) => toneColor(tone)};
    font-weight: ${({ tone }) => tone === 'default' ? 400 : 700};
`;

const TimerValue = styled.span`
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${({ tone }) => toneColor(tone)};
`;

const ProgressTrack = styled.div`
    width: 100%;
    height: 3px;
    border-radius: 2px;
    background: ${colors.neutralDark};
    overflow: hidden;
`;

const ProgressFill = styled.div`
    height: 100%;
    border-radius: 2px;
    width: ${({ progress }) => Math.max(0, Math.min(1, progress)) * 100}%;
    background: ${({ tone }) => tone === 'default' ? colors.occasionalPurple : toneColor(tone)};
    transition: width 1s linear;
`;

const FooterSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 24px;
`;

const VerifyButton = styled.button`
    min-height: 44px;
    padding: 10px 18px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    background: transparent;
    color: ${colors.occasionalPurple};
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #FAF5FF;
    }

    &:disabled {
        color: ${colors.night};
        cursor: progress;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const FeedbackText = styled.p`
    margin: 10px 0 0;
    font-size: 13px;
    line-height: 1.5;
    text-align: center;
    color: ${colors.night};
    max-width: 420px;
`;

const SafeExitNote = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid ${colors.neutralDark};
    width: 100%;
    font-size: 13px;
    line-height: 1.6;
    color: ${colors.night};

    svg {
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

const SafeExitLink = styled(Link)`
    color: ${colors.occasionalPurple};
    font-weight: 700;
    text-decoration: underline;
`;

const VisuallyHidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
`;
